import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Space, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import Api from '../../Network';
import './style.scss';

interface PaymentMethodModalProps {
  visible: boolean;
  mode: 'create' | 'edit';
  paymentMethod: any;
  onClose: () => void;
  onSuccess?: () => void;
}

const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({
  visible,
  mode,
  paymentMethod,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Create payment method mutation
  const createPaymentMethodMutation = useMutation({
    mutationFn: (data: any) => Api.post('/api/admin/payment-methods', data),
    onSuccess: () => {
      message.success(t('paymentMethods.addSuccess', 'Payment method added successfully'));
      form.resetFields();
      onSuccess?.();
      onClose();
    },
    onError: (error: any) => {
      if (error?.response?.status === 422) {
        const errors = error?.response?.data?.errors || {};
        Object.keys(errors).forEach((key) => {
          form.setFields([
            {
              name: key,
              errors: Array.isArray(errors[key]) ? errors[key] : [errors[key]],
            },
          ]);
        });
      } else {
        const errorMessage =
          error?.response?.data?.message || t('common.error', 'Error adding payment method');
        message.error(errorMessage);
      }
    },
  });

  const updatePaymentMethodMutation = useMutation({
    mutationFn: (data: any) => Api.put(`/api/admin/payment-methods/${paymentMethod?.id}`, data),
    onSuccess: () => {
      message.success(t('paymentMethods.updateSuccess', 'Payment method updated successfully'));
      form.resetFields();
      onSuccess?.();
      onClose();
    },
    onError: (error: any) => {
      if (error?.response?.status === 422) {
        const errors = error?.response?.data?.errors || {};
        Object.keys(errors).forEach((key) => {
          form.setFields([
            {
              name: key,
              errors: Array.isArray(errors[key]) ? errors[key] : [errors[key]],
            },
          ]);
        });
      } else {
        const errorMessage =
          error?.response?.data?.message || t('common.error', 'Error updating payment method');
        message.error(errorMessage);
      }
    },
  });

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      if (mode === 'create') {
        await createPaymentMethodMutation.mutateAsync(values);
      } else if (mode === 'edit') {
        await updatePaymentMethodMutation.mutateAsync(values);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  // Clear form errors when user starts typing
  const handleFieldChange = () => {
    form.setFields(
      form.getFieldsError().map((field) => ({
        name: field.name,
        errors: [],
      }))
    );
  };

  // Set form values when editing
  useEffect(() => {
    if (visible && paymentMethod && mode === 'edit') {
      form.setFieldsValue({
        name_en: paymentMethod.name_en,
        name_ar: paymentMethod.name_ar,
      });
    } else if (visible && mode === 'create') {
      form.resetFields();
    }
  }, [visible, paymentMethod, mode, form]);

  const getModalTitle = () => {
    switch (mode) {
      case 'create':
        return t('paymentMethods.addNew', 'Add New Payment Method');
      case 'edit':
        return t('paymentMethods.edit', 'Edit Payment Method');
      default:
        return '';
    }
  };

  return (
    <Modal
      title={getModalTitle()}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFieldsChange={handleFieldChange}
      >
        <Form.Item
          name="name_en"
          label={t('paymentMethods.form.nameEn', 'Name (English)')}
          rules={[{ required: true, message: t('validation.required', 'This field is required') }]}
        >
          <Input
            placeholder={t('paymentMethods.form.nameEnPlaceholder', 'Enter name in English')}
          />
        </Form.Item>

        <Form.Item
          name="name_ar"
          label={t('paymentMethods.form.nameAr', 'Name (Arabic)')}
          rules={[{ required: true, message: t('validation.required', 'This field is required') }]}
        >
          <Input
            placeholder={t('paymentMethods.form.nameArPlaceholder', 'Enter name in Arabic')}
            dir="rtl"
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>{t('common.cancel', 'Cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {mode === 'create' ? t('common.add', 'Add') : t('common.update', 'Update')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PaymentMethodModal;
